import { useState, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  phonePrefix: "+41",
  companyName: "",
  activityDomain: "",
  address: "",
  postalCode: "",
  city: "",
};

export const useDemoForm = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  const [formData, setFormData] = useState(INITIAL_FORM);

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setGeneralError("");
  }, []);

  const validateStep1 = useCallback(() => {
    const e = {};
    if (!formData.firstName.trim()) e.firstName = "Requis";
    if (!formData.lastName.trim()) e.lastName = "Requis";
    if (!formData.email.trim()) {
      e.email = "Requis";
    } else if (!formData.email.includes("@") || !formData.email.includes(".")) {
      e.email = "Email invalide";
    }
    if (!formData.phone.trim()) e.phone = "Requis";
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [formData]);

  const validateStep2 = useCallback(() => {
    const e = {};
    if (!formData.companyName.trim()) e.companyName = "Requis";
    if (!formData.activityDomain) e.activityDomain = "Requis";
    if (!formData.address.trim()) e.address = "Requis";
    if (!formData.postalCode.trim()) e.postalCode = "Requis";
    if (!formData.city.trim()) e.city = "Requis";
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [formData]);

  // Build full phone number from prefix + local number
  const getFullPhone = useCallback(() => {
    const local = formData.phone.replace(/^0+/, "").replace(/\s/g, "");
    return `${formData.phonePrefix}${local}`;
  }, [formData.phone, formData.phonePrefix]);

  // Step 1: Save partial lead to Loomy (name + email + phone)
  const submitStep1 = useCallback(async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;
    setIsSubmitting(true);
    setGeneralError("");
    try {
      await axios.post(`${API}/api/loomy/leads`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: getFullPhone(),
        email: formData.email,
        source: "Site web Lead Artisan - Etape 1",
        tags: ["lead_artisan", "step1_partial"],
      });
      setStep(2);
    } catch {
      // Save failed but still move to step 2 — better UX
      setStep(2);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateStep1, getFullPhone]);

  // Step 2: Send complete lead to Loomy
  const submitStep2 = useCallback(async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;
    setIsSubmitting(true);
    setGeneralError("");
    try {
      const response = await axios.post(`${API}/api/loomy/leads`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: getFullPhone(),
        email: formData.email,
        company: formData.companyName,
        industry: formData.activityDomain,
        address: formData.address,
        postal_code: formData.postalCode,
        city: formData.city,
        source: "Site web Lead Artisan",
        tags: ["lead_artisan", "demo_request", "complete"],
      });
      if (response.data.success) {
        if (typeof window.gtag_report_conversion === "function") {
          window.gtag_report_conversion();
        }
        setIsSuccess(true);
      } else {
        setGeneralError("Une erreur est survenue. Veuillez reessayer.");
      }
    } catch (err) {
      if (err.response) {
        const detail = err.response.data?.detail || err.response.data?.message || "";
        if (detail.includes("Loomy") || detail.includes("CRM") || detail.includes("404")) {
          setGeneralError("Service temporairement indisponible. Veuillez reessayer dans quelques instants ou nous contacter directement.");
        } else {
          setGeneralError("Une erreur est survenue lors de l'envoi. Veuillez reessayer.");
        }
      } else if (err.request) {
        setGeneralError("Erreur de connexion. Verifiez votre connexion internet.");
      } else {
        setGeneralError("Une erreur est survenue. Veuillez reessayer.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateStep2, getFullPhone]);

  const goBack = useCallback(() => {
    setStep(1);
    setErrors({});
    setGeneralError("");
  }, []);

  const resetForm = useCallback(() => {
    setStep(1);
    setFormData(INITIAL_FORM);
    setErrors({});
    setGeneralError("");
    setIsSuccess(false);
  }, []);

  const getInputClass = useCallback((field) => {
    return errors[field]
      ? "border-accent focus-visible:ring-accent"
      : "border-slate-200 focus-visible:ring-primary";
  }, [errors]);

  return {
    step,
    formData,
    errors,
    generalError,
    isSubmitting,
    isSuccess,
    handleChange,
    submitStep1,
    submitStep2,
    goBack,
    resetForm,
    getInputClass,
  };
};
